import { FC, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import type { HgProductCategory } from "@/types/taxonomy";

interface CategoryOption {
  id: string;
  name: string;
  depth: number;
  fullPath: string;
}

const ROOT_PARENT_ID = "0";

function buildOptions(categories: HgProductCategory[]): CategoryOption[] {
  const childrenMap = new Map<string, HgProductCategory[]>();
  const roots: HgProductCategory[] = [];

  for (const cat of categories) {
    const parentId = cat.parentId || ROOT_PARENT_ID;
    if (parentId === ROOT_PARENT_ID) {
      roots.push(cat);
    } else {
      const siblings = childrenMap.get(parentId) ?? [];
      siblings.push(cat);
      childrenMap.set(parentId, siblings);
    }
  }

  const result: CategoryOption[] = [];

  function walk(items: HgProductCategory[], depth: number) {
    for (const item of items) {
      result.push({
        id: item.id,
        name: item.name,
        depth,
        fullPath: item.nameTree?.join(" > ") ?? item.name,
      });
      const children = childrenMap.get(item.id);
      if (children) walk(children, depth + 1);
    }
  }

  walk(roots, 0);
  return result;
}

interface ProductCategorySingleSelectProps {
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string | null) => void;
  label?: string;
  inputTestId?: string;
}

export const ProductCategorySingleSelect: FC<
  ProductCategorySingleSelectProps
> = ({
  selectedCategoryId,
  onCategorySelect,
  label = "Product Category",
  inputTestId,
}) => {
  const { productCategories, productCategoriesLoading } = useTaxonomyContext();

  const options = useMemo(
    () => buildOptions(productCategories),
    [productCategories]
  );

  const selectedOption =
    options.find((o) => o.id === selectedCategoryId) ?? null;

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedOption}
      onChange={(_, option) => onCategorySelect(option?.id ?? null)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      openOnFocus
      loading={productCategoriesLoading}
      noOptionsText={
        productCategoriesLoading ? "Loading..." : "No categories found"
      }
      slotProps={{
        listbox: { sx: { maxHeight: 300 } },
      }}
      renderOption={({ key, ...props }, option) => (
        <Box
          component="li"
          key={key}
          {...props}
          sx={{ pl: `${8 + option.depth * 16}px !important` }}
        >
          <Typography variant="body2">{option.name}</Typography>
        </Box>
      )}
      renderInput={({ inputProps, ...params }) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search..."
          slotProps={{
            htmlInput: {
              ...inputProps,
              ...(inputTestId ? { "data-testid": inputTestId } : {}),
            },
          }}
        />
      )}
    />
  );
};
